// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/App.js

import React, { useState, useEffect, useCallback } from 'react';

// --- Imports de Autenticação e Firestore ---
import { auth, db } from './firebase';
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    onAuthStateChanged,
    signOut
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore'; 

import LoginView from './components/auth/LoginView';
import CriarContaView from './components/auth/CriarContaView';

// --- Seus Imports Existentes ---
import { useInventory } from './hooks/useInventory';
import { useSales } from './hooks/useSales';
import { useBrands } from './hooks/useBrands';
import { useCustomers } from './hooks/useCustomers';
import GlobalStyles from './components/GlobalStyles';
import Header from './components/Header';
import DashboardView from './components/views/DashboardView';
import InventoryView from './components/views/InventoryView';
import RegistrosView from './components/views/RegistrosView';
import SalesView from './components/views/SalesView';
import CustomersView from './components/views/CustomersView';
import CustomerDetailView from './components/views/CustomerDetailView';
import FinanceiroView from './components/views/FinanceiroView';
import AdminView from './components/views/AdminView';
import AddProductModal from './components/modals/AddProductModal';
// NOVO MODAL DE EDIÇÃO COMPLETA DO PRODUTO:
import EditProductModal from './components/modals/EditProductModal';
import ManageBrandsModal from './components/modals/ManageBrandsModal';
import ConfirmDeleteModal from './components/modals/ConfirmDeleteModal';
import EditPriceModal from './components/modals/EditPriceModal';
import RegisterSaleModal from './components/modals/RegisterSaleModal';
import AddCustomerModal from './components/modals/AddCustomerModal';
import EditCustomerModal from './components/modals/EditCustomerModal';
import SelectCustomerModal from './components/modals/SelectCustomerModal';
import AddPaymentModal from './components/modals/AddPaymentModal';
import CustomerLandingPage from './components/views/CustomerLandingPage';

// --- Imports de Compromissos (Contas a Pagar) ---
import { usePurchases } from './hooks/usePurchases';
import CompromissosView from './components/views/CompromissosView';
import AddPurchaseModal from './components/modals/AddPurchaseModal';
import AddExpensePaymentModal from './components/modals/AddExpensePaymentModal';
// NOVOS MODAIS DE EDIÇÃO DE COMPROMISSOS ADICIONADOS:
import EditPurchaseModal from './components/modals/EditPurchaseModal';
import EditExpensePaymentModal from './components/modals/EditExpensePaymentModal';

// --- Imports de Tesouraria ---
import { useTreasury } from './hooks/useTreasury';
import DashboardFinanceiroView from './components/views/DashboardFinanceiroView';
import AddTreasuryAdjustmentModal from './components/modals/AddTreasuryAdjustmentModal';
import EditTreasuryAdjustmentModal from './components/modals/EditTreasuryAdjustmentModal';

// URL base das suas Cloud Functions
const FUNCTIONS_BASE_URL = 'https://us-central1-meu-controle-de-estoque-2.cloudfunctions.net';

export default function App() {
    // --- ESTADO DE AUTENTICAÇÃO ---
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [authStatus, setAuthStatus] = useState('checking');
    const [userStatus, setUserStatus] = useState(null);
    const [authView, setAuthView] = useState('login'); 

    // --- Seus Estados Existentes ---
    const [currentView, setView] = useState('dashboard');
    const [modalState, setModalState] = useState({ type: null, data: null });
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [saleInProgress, setSaleInProgress] = useState(null); 
    const [selectedIds, setSelectedIds] = useState([]);
    const [scrollTo, setScrollTo] = useState(null);
    
    // Hooks de dados (AGORA IMPORTANDO O updateProduct)
    const { inventory, inventoryMetrics, addProduct, deleteProduct, updateProductPrice, deleteMultipleProducts, bulkAddProducts, updateProduct } = useInventory(user?.uid);
    const { sales, addSale, deleteSale, metrics, deleteMultipleSales, addPaymentToSale, financialFlow } = useSales(inventory, user?.uid);
    const { brands, addBrand, deleteBrand } = useBrands(user?.uid);
    const { customers, addCustomer, updateCustomer, deleteCustomer, deleteMultipleCustomers } = useCustomers(user?.uid);
    
    // Hook de Compromissos (AGORA IMPORTANDO TODAS AS FUNÇÕES DE EDIÇÃO E EXCLUSÃO)
    const { purchases, purchaseFlow, addPurchase, deletePurchase, updatePurchase, deleteMultiplePurchases,
        addPaymentToPurchase, bulkAddPaymentsToPurchases, deleteExpensePayment, deleteMultipleExpensePayments, updateExpensePayment 
    } = usePurchases(user?.uid);

    // Hook de Tesouraria
    const { treasuryData, addAdjustment, updateAdjustment, deleteAdjustment, deleteMultipleAdjustments } = useTreasury(user?.uid, financialFlow, purchaseFlow);

    // --- EFEITO PARA OUVIR O ESTADO DE AUTENTICAÇÃO ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            setIsAdmin(false);
            
            if (currentUser) {
                setUserStatus('loading');
                setAuthStatus('signedIn');

                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                    setUserStatus(userDoc.data().status);
                } else {
                    setUserStatus('not_found');
                }

                const idTokenResult = await currentUser.getIdTokenResult(true);
                if (idTokenResult.claims.admin) {
                    setIsAdmin(true);
                }
            } else {
                setAuthStatus('signedOut');
                setUserStatus(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // --- FUNÇÕES DE AUTENTICAÇÃO ATUALIZADAS ---
    const handleRegister = async (email, password) => {
        try {
            const response = await fetch(`${FUNCTIONS_BASE_URL}/checkBlocklist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            if (!response.ok) {
                throw new Error("Falha ao comunicar com o serviço de verificação.");
            }
            const data = await response.json();
            if (data.isBlocked) {
                const blockError = new Error("Este e-mail foi bloqueado e não pode ser registado.");
                blockError.code = 'auth/email-blocked';
                throw blockError;
            }
        } catch (error) {
            console.error("Erro ao verificar a blocklist:", error);
            throw error; 
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
                email: user.email,
                createdAt: serverTimestamp(),
                status: 'pendente'
            });
            return userCredential;
        } catch (error) {
            console.error("ERRO NO PROCESSO DE REGISTO:", error);
            alert(`Ocorreu um erro ao registar. Verifique o console. Erro: ${error.message}`);
            throw error;
        }
    };

    const handleLogin = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const handleLogout = () => {
        signOut(auth).then(() => {
            setView('dashboard');
        });
    };

    // --- LÓGICA DE MODAIS E NAVEGAÇÃO ---
    const clearSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    useEffect(() => {
        clearSelection();
    }, [currentView, clearSelection]);

    const handleOpenModal = useCallback((type, data = {}) => {
        if (type === 'selectCustomer' && data.salePayload) {
            setSaleInProgress(data.salePayload);
        }
        setModalState({ type, data });
    }, []);
    
    const handleCloseModal = useCallback(() => {
        setModalState({ type: null, data: null });
        setSaleInProgress(null);
    }, []);
                                                                           
    const handleNavigate = useCallback((view, targetId = null) => {
        setView(view);
        setScrollTo(targetId);
    }, []);

    // --- RESTANTE DOS SEUS HANDLERS ---
    const handleSelectOne = useCallback((id) => {
        setSelectedIds(prevIds => 
            prevIds.includes(id) ? prevIds.filter(prevId => prevId !== id) : [...prevIds, id]
        );
    }, []);

    const handleSelectAll = useCallback((pageItemIds, isAllSelectedOnPage) => {
        setSelectedIds(prevIds => {
            const newIds = new Set(prevIds);
            if (isAllSelectedOnPage) {
                pageItemIds.forEach(id => newIds.delete(id));
            } else {
                pageItemIds.forEach(id => newIds.add(id));
            }
            return Array.from(newIds);
        });
    }, []);

    const handleDeleteSelected = useCallback(() => {
        if (selectedIds.length === 0) return;
        handleOpenModal('confirmDelete', {
            type: 'bulk',
            count: selectedIds.length,
            view: currentView 
        });
    }, [selectedIds, currentView, handleOpenModal]);

    // O CÉREBRO DA EXCLUSÃO: ATUALIZADO PARA LIDAR COM COMPRAS EM LOTE
    const handleConfirmDelete = useCallback(async () => {
        const itemToDelete = modalState.data;
        if (!itemToDelete) return;
        try {
            if (itemToDelete.type === 'bulk') {
                if (itemToDelete.view === 'registros') await deleteMultipleProducts(selectedIds);
                else if (itemToDelete.view === 'customers') await deleteMultipleCustomers(selectedIds);
                else if (itemToDelete.view === 'sales') await deleteMultipleSales(selectedIds);
                else if (itemToDelete.view === 'compromissos') await deleteMultipleExpensePayments(selectedIds); 
                else if (itemToDelete.view === 'tesouraria') await deleteMultipleAdjustments(selectedIds); 
                clearSelection();
            } else if (itemToDelete.type === 'bulkPurchases') {
                // EXCLUSÃO EM LOTE DE CONTAS A PAGAR (COMPRAS INTEIRAS)
                await deleteMultiplePurchases(itemToDelete.ids);
                clearSelection();
            } else if (itemToDelete.type === 'product') await deleteProduct(itemToDelete.id);
            else if (itemToDelete.type === 'sale') await deleteSale(itemToDelete.id);
            else if (itemToDelete.type === 'customer') await deleteCustomer(itemToDelete.id);
            else if (itemToDelete.type === 'treasuryAdjustment') await deleteAdjustment(itemToDelete.id);
            else if (itemToDelete.type === 'purchase') await deletePurchase(itemToDelete.id);
            else if (itemToDelete.type === 'expensePayment') await deleteExpensePayment(itemToDelete.purchaseId, itemToDelete.id);
        } catch (error) {
            console.error("Falha ao deletar item:", error);
        } finally {
            handleCloseModal();
        }
    }, [modalState.data, selectedIds, deleteMultipleProducts, deleteMultipleCustomers, deleteMultipleSales, deleteMultipleExpensePayments, deleteMultipleAdjustments, deleteMultiplePurchases, deleteProduct, deleteSale, deleteCustomer, deleteAdjustment, deletePurchase, deleteExpensePayment, handleCloseModal, clearSelection]);
    
    const handleSelectCustomer = (customerId) => {
        setSelectedCustomerId(customerId);
        setView('customerDetail');
    };

    const handleBackToCustomers = () => {
        setSelectedCustomerId(null);
        setView('customers');
    };
    
    const handleCustomerSelectedForSale = useCallback((customer) => {
        setModalState({ 
            type: 'registerSale', 
            data: { 
                selectedCustomer: customer, 
                payload: saleInProgress 
            } 
        });
        setSaleInProgress(null);
    }, [saleInProgress]);

    const handleOpenAddCustomerFromSale = useCallback(() => {
        handleOpenModal('addCustomer', { fromSale: true });
    }, [handleOpenModal]);

    const pathname = window.location.pathname;
    const isLandingPage = pathname.startsWith('/cadastro/');

    if (isLandingPage) {
        return <CustomerLandingPage />;
    }

    function renderCurrentView() {
        const scrollProps = { scrollTo, onScrollComplete: () => setScrollTo(null) };
        switch (currentView) {
            case 'dashboard':
                return <DashboardView openModal={handleOpenModal} navigateTo={handleNavigate} />;
            case 'inventory':
                return <InventoryView inventory={inventory} inventoryMetrics={inventoryMetrics} sales={sales} />;
            case 'registros':
                return <RegistrosView inventory={inventory} brands={brands} openModal={handleOpenModal} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onDeleteSelected={handleDeleteSelected} addProduct={addProduct} bulkAddProducts={bulkAddProducts} />;
            case 'sales':
                 return <SalesView sales={sales} openModal={handleOpenModal} metrics={metrics} inventory={inventory} brands={brands} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onDeleteSelected={handleDeleteSelected} onCustomerSelect={handleSelectCustomer} {...scrollProps} />;
            case 'customers':
                return <CustomersView
                 userId={user?.uid} 
                 userSettings={{ isLandingPageActive: userStatus === 'ativo' }} 
                 customers={customers} sales={sales} openModal={handleOpenModal} onCustomerSelect={handleSelectCustomer} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onDeleteSelected={handleDeleteSelected} {...scrollProps}/>;
            case 'customerDetail':
                const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
                return <CustomerDetailView customer={selectedCustomer} sales={sales} onBack={handleBackToCustomers} openModal={handleOpenModal} />;
            case 'financeiro':
                return <FinanceiroView financialFlow={financialFlow} openModal={handleOpenModal} sales={sales} {...scrollProps} />;
            case 'compromissos':
                return <CompromissosView purchaseFlow={purchaseFlow} openModal={handleOpenModal} purchases={purchases} bulkAddPaymentsToPurchases={bulkAddPaymentsToPurchases} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onDeleteSelected={handleDeleteSelected} {...scrollProps} />;
            case 'tesouraria':
                return <DashboardFinanceiroView treasuryData={treasuryData} openModal={handleOpenModal} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onDeleteSelected={handleDeleteSelected} />;
            case 'admin':
                return <AdminView />;
            default:
                return <DashboardView openModal={handleOpenModal} navigateTo={handleNavigate} />;
        }
    }

    function renderModals() {
        return (
            <>
                <AddProductModal isOpen={modalState.type === 'addProduct'} onClose={handleCloseModal} onSave={addProduct} brands={brands} />
                <EditProductModal isOpen={modalState.type === 'editProduct'} onClose={handleCloseModal} product={modalState.data} onSave={updateProduct} brands={brands} />
                <ManageBrandsModal isOpen={modalState.type === 'manageBrands'} onClose={handleCloseModal} brands={brands} onAddBrand={addBrand} onDeleteBrand={deleteBrand} inventory={inventory}/>
                <RegisterSaleModal isOpen={modalState.type === 'registerSale'} onClose={handleCloseModal} inventory={inventory} brands={brands} customers={customers} onSave={addSale} openSelectCustomerModal={(salePayload) => handleOpenModal('selectCustomer', { salePayload })} initialData={modalState.data}/>
                <ConfirmDeleteModal isOpen={modalState.type === 'confirmDelete'} onClose={handleCloseModal} item={modalState.data} onConfirm={handleConfirmDelete} />
                <EditPriceModal isOpen={modalState.type === 'editPrice'} onClose={handleCloseModal} product={modalState.data} onSave={updateProductPrice} />
                <AddCustomerModal 
                    isOpen={modalState.type === 'addCustomer'} 
                    onClose={(newCustomer) => {
                        if (newCustomer && newCustomer.id && saleInProgress) {
                            setModalState({ 
                                type: 'registerSale', 
                                data: { 
                                    selectedCustomer: newCustomer, 
                                    payload: saleInProgress 
                                } 
                            });
                            setSaleInProgress(null); 
                        } else {
                            setModalState({ type: null, data: null });
                            setSaleInProgress(null);
                        }
                    }} 
                    onSave={async (data) => {
                        try {
                            const docRef = await addCustomer(data);
                            return { id: docRef.id, ...data };
                        } catch (error) {
                            throw error;
                        }
                    }} 
                />
                <EditCustomerModal isOpen={modalState.type === 'editCustomer'} onClose={handleCloseModal} customer={modalState.data} onSave={updateCustomer} />
                <SelectCustomerModal isOpen={modalState.type === 'selectCustomer'} onClose={() => handleOpenModal('registerSale')} customers={customers} onSelect={handleCustomerSelectedForSale} onAddNew={handleOpenAddCustomerFromSale} />
                <AddPaymentModal isOpen={modalState.type === 'addPayment'} onClose={handleCloseModal} sale={modalState.data} onSave={addPaymentToSale} />
                
                {/* Modais de Compromissos */}
                <AddPurchaseModal isOpen={modalState.type === 'addPurchase'} onClose={handleCloseModal} onSave={addPurchase} />
                <EditPurchaseModal isOpen={modalState.type === 'editPurchase'} onClose={handleCloseModal} onSave={updatePurchase} purchase={modalState.data} />
                <AddExpensePaymentModal isOpen={modalState.type === 'addExpensePayment'} onClose={handleCloseModal} onSave={addPaymentToPurchase} purchase={modalState.data} />
                <EditExpensePaymentModal isOpen={modalState.type === 'editExpensePayment'} onClose={handleCloseModal} onSave={updateExpensePayment} paymentItem={modalState.data} />

                {/* Modais da Tesouraria */}
                <AddTreasuryAdjustmentModal isOpen={modalState.type === 'addTreasuryAdjustment'} onClose={handleCloseModal} onSave={addAdjustment} />
                <EditTreasuryAdjustmentModal isOpen={modalState.type === 'editTreasuryAdjustment'} onClose={handleCloseModal} onSave={updateAdjustment} adjustment={modalState.data} />
            </>
        )
    }

    // --- LÓGICA DE RENDERIZAÇÃO PRINCIPAL ---
    if (authStatus === 'checking' || (authStatus === 'signedIn' && userStatus === 'loading')) {
        return <div className="flex items-center justify-center min-h-screen"><p>A carregar...</p></div>;
    }

    if (authStatus === 'signedOut') {
        return authView === 'login' ? (
            <LoginView onLogin={handleLogin} onNavigateToRegister={() => setAuthView('register')} />
        ) : (
            <CriarContaView onRegister={handleRegister} onNavigateToLogin={() => setAuthView('login')} />
        );
    }
    
    if (authStatus === 'signedIn' && userStatus === 'pendente') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Conta Criada com Sucesso!</h2>
                <p className="text-gray-600 mb-6">A sua conta está aguardando a aprovação de um administrador. <br/> Por favor, aguarde.</p>
                <button onClick={handleLogout} className="custom-button py-2 px-6 font-semibold rounded-lg shadow-sm">
                    Sair
                </button>
            </div>
        );
    }

    if (authStatus === 'signedIn' && userStatus === 'ativo') {
        return (
            <>
                <GlobalStyles />
                <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                    <Header currentView={currentView} setView={setView} onLogout={handleLogout} isAdmin={isAdmin} userEmail={user?.email} />
                    <main>
                        {renderCurrentView()} 
                    </main>
                    {renderModals()} 
                </div>
            </>
        );
    }
    
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-4">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Ocorreu um Erro</h2>
            <p className="text-gray-600 mb-6">Não foi possível verificar o estado da sua conta. <br/> Por favor, tente entrar novamente.</p>
            <button onClick={handleLogout} className="custom-button">Sair</button>
        </div>
    );
}